import type { Cardinality } from '@/Enums/Enums';
import { Status } from '@/Enums/Enums';
import type {
    DVGItem,
    ResourceItem,
    TypeMeta,
    Property,
    Association,
} from '@/Typings';
import type { PropertyRow, Dictionary } from '@/Datastructures';
import type {
    LabelViewModel as LabelViewModelT,
    MLStringPopupBoxViewModel as MLStringPopupBoxViewModelT,
    MultilineTextBoxViewModel as MultilineTextBoxViewModelT,
    ControlViewModel,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    Utilities,
    RADApplication,
    ComponentManager,
    TextBoxViewModel as TextBoxViewModelT,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type ExtensionEditorViewModel from '@/ViewModels/Controls/ExtensionEditorViewModel';

interface NameTextBoxViewModelT extends TextBoxViewModelT {
    onFocusOut?(): void;
    nameFinalizedHandler?(name: string): void;
}

class MemberTabViewModel {
    protected LabelViewModel =
        ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    protected TextBoxViewModel =
        ComponentManager.getViewModel<typeof TextBoxViewModelT>(
            'TextBoxViewModel'
        );
    protected MultilineTextBoxViewModel = ComponentManager.getViewModel<
        typeof MultilineTextBoxViewModelT
    >('MultilineTextBoxViewModel');
    protected MLStringPopupBoxViewModel = ComponentManager.getViewModel<
        typeof MLStringPopupBoxViewModelT
    >('MLStringPopupBoxViewModel');

    private NameLabel: LabelViewModelT;
    private ErgonomicIdentifierLabel: LabelViewModelT;
    private TooltipLabel: LabelViewModelT;
    private HelpLabel: LabelViewModelT;

    public Name: NameTextBoxViewModelT;
    protected ErgonomicIdentifier: MLStringPopupBoxViewModelT;
    protected Tooltip: MLStringPopupBoxViewModelT;
    protected Help: MLStringPopupBoxViewModelT;

    protected relatedData: PropertyRow;
    protected currentLanguage: string;

    protected fieldsWithCustomEnabledState: Array<ControlViewModel>;
    protected nonEditableStaticPropertyFields: Array<ControlViewModel>;
    protected editableStaticPropertyFields: Array<ControlViewModel>;

    public isInUpdateMode = false;

    // injected
    public DVGItem: DVGItem = null;
    public ResourceItems: Dictionary<ResourceItem> = null;
    public context: ExtensionEditorViewModel;
    public typeMeta: TypeMeta;

    protected subscriptions: Array<KnockoutSubscription> = [];

    public constructor(protected locator: KnockoutObservable<string>) {
        this.currentLanguage = RADApplication.getInstance().currentLanguage();

        this.createName();
        this.createErgonomicIdentifier();
        this.createTooltip();
        this.createHelp();
    }

    protected updateContent() {
        this.updateMLStringPopupBox(
            this.ErgonomicIdentifier,
            'ErgNameByLanguage'
        );
        this.updateMLStringPopupBox(this.Help, 'HelpByLanguage');
        this.updateMLStringPopupBox(this.Tooltip, 'TooltipByLanguage');
    }

    protected updateMLStringPopupBox(
        viewModel: MLStringPopupBoxViewModelT,
        propertyRowKeyForValueByLanguage: string
    ) {
        // Prüfen, ob Flag "isLanguageEqual" gesetzt werden muss
        let isEachLanguageEqual = true;
        const persistentTranslationCount = _.keys(
            this.relatedData[propertyRowKeyForValueByLanguage]
        ).length;
        const persistentTranslationsCountIsDifferentToLanguageCount =
            persistentTranslationCount > 0 &&
            viewModel.groups().length !== persistentTranslationCount;
        const uniqueValues = _.uniq(
            _.values(this.relatedData[propertyRowKeyForValueByLanguage])
        );
        if (
            persistentTranslationsCountIsDifferentToLanguageCount ||
            uniqueValues.length > 1
        )
            isEachLanguageEqual = false;

        viewModel.isEachLanguageEqual(isEachLanguageEqual);

        viewModel.groups().forEach(group => {
            if (!this.DVGItem) {
                viewModel.multilineTextBox.value('');
                return;
            }

            let language = group.language;
            let value = _.isEmpty(this.relatedData)
                ? ''
                : this.relatedData[propertyRowKeyForValueByLanguage][language];

            if (group.language == this.currentLanguage) {
                viewModel.multilineTextBox.value(value);
                return;
            }

            group.liveValue(value);
        });
    }

    /**
     * Synchronizes inputs to MultilingualStringPopupBox-Controls
     *  to the Datastructure that relates to a PropertyRow as well as
     *  to the ResourceItem for the related Property or Association
     *
     * @param viewModel any MultilingualStringPopupBox-Control related ViewModel
     * @param propertyRowKeyForResourceItem the key for which the related ResourceItem is stored in the PropertyRow/RelatedData
     * @param propertyRowKeyForValueByLanguage the key for which the multilingual value per language is stored in the PropertyRow/RelatedData
     * @param resourceIdGetter a getter that is called to get the resourceId for the related ResourceItem of the <currently> selected Property/Association
     */
    protected addMultilingualFieldSync(
        viewModel: MLStringPopupBoxViewModelT,
        propertyRowKeyForResourceItem: keyof PropertyRow,
        propertyRowKeyForValueByLanguage: keyof PropertyRow,
        resourceIdGetter: () => string
    ) {
        viewModel.groups().forEach(group => {
            let subscription = group.liveValue.subscribe(liveValueUpdate => {
                if (this.isInUpdateMode) {
                    return;
                }

                let language = group.language;
                let resourceId = resourceIdGetter();

                // sync to resourceItem
                if (Utilities.isNotBlank(resourceId)) {
                    let resourceItem = this.findOrCreateResourceItem(
                        propertyRowKeyForResourceItem,
                        resourceId,
                        language
                    );
                    this.updateResourceItem(resourceItem, liveValueUpdate);
                }

                // sync to propertyRow/relatedData
                this.relatedData[propertyRowKeyForValueByLanguage][language] =
                    liveValueUpdate;
            });
            this.subscriptions.push(subscription);
        });
    }

    public dispose() {
        this.disposeSubscriptions();
    }

    protected disposeSubscriptions() {
        if (!_.isEmpty(this.subscriptions)) {
            this.subscriptions.forEach(s => s.dispose());
        }
    }

    protected syncFocusableToDisabled(
        field: ControlViewModel | Array<ControlViewModel>
    ) {
        if (_.isArray(field)) {
            field.forEach(f => f.syncFocusableWithEnabledState());
        } else {
            field.syncFocusableWithEnabledState();
        }
    }

    protected createName() {
        this.NameLabel = new this.LabelViewModel({
            ergName: 'RADEM.RADEXTENSIONMANAGER.NAME',
            postfix: ' *',
        });
        this.Name = new this.TextBoxViewModel({
            isEnabled: false,
            locator: this.locator() + '.name',
        });
    }

    private createErgonomicIdentifier() {
        this.ErgonomicIdentifierLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.ERGNAMELABEL'
        );
        this.ErgonomicIdentifier = new this.MLStringPopupBoxViewModel({
            isMultiline: false,
            isEnabled: false,
            locator: this.locator() + '.ergNamePopup',
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: false,
                isEnabled: false,
                locator: this.locator() + '.ergNameMLTextBox',
            }),
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });
        this.ErgonomicIdentifier.link.isEnabled(false);
        this.addMultilingualFieldSync(
            this.ErgonomicIdentifier,
            'ErgonomicIdentifierResourceItems',
            'ErgNameByLanguage',
            () => this.DVGItem.Member.ErgName
        );
    }

    private createTooltip() {
        this.TooltipLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.TOOLTIP'
        );
        this.Tooltip = new this.MLStringPopupBoxViewModel({
            isMultiline: false,
            isEnabled: false,
            locator: this.locator() + '.tooltipPopup',
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: false,
                isEnabled: false,
                locator: this.locator() + '.tooltipMLTextBox',
            }),
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });
        this.Tooltip.link.isEnabled(false);

        this.addMultilingualFieldSync(
            this.Tooltip,
            'TooltipResourceItems',
            'TooltipByLanguage',
            () => {
                if (
                    Utilities.isBlank(this.DVGItem.Member.Tooltip) &&
                    !Utilities.isBlank(this.DVGItem.Member.Name)
                ) {
                    let tooltipResourceID = (
                        this.typeMeta.Name +
                        '_' +
                        this.DVGItem.Member.Name +
                        '_TOOLTIP'
                    ).toUpperCase();
                    this.DVGItem.Member.Tooltip = tooltipResourceID;
                    Status.setChanged(this.DVGItem);
                    Status.setChanged(this.DVGItem.Member);

                    let propertyOrAssociationMeta = this.context.getMetaByPdoId(
                        this.DVGItem.Member.PDO_ID
                    );
                    propertyOrAssociationMeta.Tooltip = tooltipResourceID;
                    Status.setChanged(propertyOrAssociationMeta);
                }

                return this.DVGItem.Member.Tooltip;
            }
        );
    }

    private createHelp() {
        this.HelpLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.HELPTEXT'
        );
        this.Help = new this.MLStringPopupBoxViewModel({
            isMultiline: true,
            isEnabled: false,
            locator: this.locator() + '.helptextPopup',
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: true,
                isEnabled: false,
                locator: this.locator() + '.helptextMLTextBox',
            }),
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });
        this.Help.link.isEnabled(false);

        this.addMultilingualFieldSync(
            this.Help,
            'HelptextResourceItems',
            'HelpByLanguage',
            () => {
                if (
                    Utilities.isBlank(this.DVGItem.Member.Helptext) &&
                    !Utilities.isBlank(this.DVGItem.Member.Name)
                ) {
                    let helpTextResourceID = (
                        this.typeMeta.Name +
                        '_' +
                        this.DVGItem.Member.Name +
                        '_HELPTEXT'
                    ).toUpperCase();
                    this.DVGItem.Member.Helptext = helpTextResourceID;
                    Status.setChanged(this.DVGItem);
                    Status.setChanged(this.DVGItem.Member);

                    let propertyOrAssociationMeta = this.context.getMetaByPdoId(
                        this.DVGItem.Member.PDO_ID
                    );
                    propertyOrAssociationMeta.Helptext = helpTextResourceID;
                    Status.setChanged(propertyOrAssociationMeta);
                }

                return this.DVGItem.Member.Helptext;
            }
        );
    }

    private findOrCreateResourceItem(
        resourceItemKey: string,
        resourceId: string,
        language: string
    ): ResourceItem {
        if (this.relatedData[resourceItemKey] === undefined) {
            this.relatedData[resourceItemKey] = {};
        }

        let resourceItem = this.relatedData[resourceItemKey][
            language
        ] as ResourceItem;

        if (!resourceItem && resourceId) {
            resourceItem = this.findResourceItem(resourceId, language);
        }

        if (!resourceItem) {
            resourceItem = this.createResourceItem(resourceId, language);

            if (
                this.context.resourceItemsByErgName[
                    resourceId.toLowerCase()
                ] === undefined
            ) {
                this.context.resourceItemsByErgName[resourceId.toLowerCase()] =
                    {};
            }
            this.context.resourceItemsByErgName[resourceId.toLowerCase()][
                language
            ] = resourceItem;
        }

        this.relatedData[resourceItemKey][language] = resourceItem;
        this.ResourceItems[resourceItem.PDO_ID] = resourceItem;

        return resourceItem;
    }

    private findResourceItem(
        resourceId: string,
        language: string
    ): ResourceItem {
        let resourceItem =
            this.ResourceItems[
                Utilities.findFirst(Object.keys(this.ResourceItems), key => {
                    let item = this.ResourceItems[key];
                    return (
                        item.ResourceID == resourceId &&
                        item.ISO6391 == language &&
                        item.Status != Status.Deleted
                    );
                })
            ];
        return resourceItem;
    }

    private createResourceItem(resourceId: string, language: string) {
        return {
            PDO_ID: this.context.getNextPdoId(),
            Status: Status.New,
            ISO6391: language,
            ResourceID: resourceId,
            Value: null,
        };
    }

    private updateResourceItem(item: ResourceItem, newValue: string) {
        item.Value = newValue;
        Status.setChanged(item);

        this.context.updateResourceItem(
            item.ResourceID,
            item.ISO6391,
            newValue
        );
    }

    protected isProperty(obj: Property | Association) {
        return _.contains(Object.keys(obj), 'Required');
    }
}

interface DataTypeOption {
    pdoId: string;
    objectIdentValue: string;
}

export default MemberTabViewModel;
